/**
 * Safe date formatting utilities
 * Prevents "Invalid Date" from showing in the UI
 */

/**
 * Safely format a date to locale string
 * @param {string|Date|null|undefined} date - Date to format
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Formatted date or fallback
 */
export function formatDate(date, fallback = 'N/A') {
    if (!date) return fallback;

    try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return fallback;

        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch (error) {
        return fallback;
    }
}

/**
 * Safely format a date to locale date and time string
 * @param {string|Date|null|undefined} date - Date to format
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Formatted date and time or fallback
 */
export function formatDateTime(date, fallback = 'N/A') {
    if (!date) return fallback;

    try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return fallback;

        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    } catch (error) {
        return fallback;
    }
}

/**
 * Safely format a date to time only
 * @param {string|Date|null|undefined} date - Date to format
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Formatted time or fallback
 */
export function formatTime(date, fallback = 'N/A') {
    if (!date) return fallback;

    try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return fallback;

        return d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
        });
    } catch (error) {
        return fallback;
    }
}

/**
 * Safely format a date to relative time (e.g., "2 hours ago")
 * @param {string|Date|null|undefined} date - Date to format
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Relative time or fallback
 */
export function formatRelativeTime(date, fallback = 'N/A') {
    if (!date) return fallback;

    try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return fallback;

        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

        return formatDate(date, fallback);
    } catch (error) {
        return fallback;
    }
}

/**
 * Check if a date is valid
 * @param {string|Date|null|undefined} date - Date to check
 * @returns {boolean} True if date is valid
 */
export function isValidDate(date) {
    if (!date) return false;

    try {
        const d = new Date(date);
        return !isNaN(d.getTime());
    } catch (error) {
        return false;
    }
}
